<script setup>
import {
  computed,
  watch,
  nextTick,
  shallowRef,
  ref,
  onMounted,
  onUnmounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LPopup } from '@vue-leaflet/vue-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

if (typeof window !== 'undefined') {
  window.L = L;
}

const props = defineProps({
  locations: {
    type: Array,
    default: () => [],
  },
  spaces: {
    type: Array,
    default: () => [],
  },
  showBasemapToggle: {
    type: Boolean,
    default: true,
  },
  showLegend: {
    type: Boolean,
    default: false,
  },
  legendItems: {
    type: Array,
    default: () => [],
  },
  spaceMarkerLabel: {
    type: String,
    default: '',
  },
  basemapLabel: {
    type: String,
    default: 'Map style',
  },
  calmLabel: {
    type: String,
    default: 'Calm',
  },
  standardLabel: {
    type: String,
    default: 'Standard',
  },
  mapClass: {
    type: String,
    default: 'h-80',
  },
});

const { t } = useI18n();

const BRAND_PURPLE = '#7045AE';
const BRAND_SOFT = '#EFEDF8';
const DEFAULT_PIN_COLOR = '#64748B';

const BASEMAPS = {
  calm: {
    light: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  standard: {
    light: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    dark: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
};

const mapRef = ref(null);
const leafletObject = ref(null);
const spaceIcon = shallowRef(null);
const isDark = ref(false);
const basemapStyle = ref('calm');
let themeObserver = null;
let clusterGroup = null;
let clusterReady = false;

const zoom = ref(5);
const center = ref([50.5, 4.5]);

const activeBasemap = computed(
  () => BASEMAPS[basemapStyle.value] || BASEMAPS.calm
);

const tileUrl = computed(() =>
  isDark.value ? activeBasemap.value.dark : activeBasemap.value.light
);

const tileAttribution = computed(() => activeBasemap.value.attribution);

const validLocations = computed(() =>
  (props.locations || []).filter(
    location => location?.lat != null && location?.lng != null
  )
);

const validSpaces = computed(() =>
  (props.spaces || []).filter(
    space => space?.lat != null && space?.lng != null
  )
);

const hasMapContent = computed(
  () => validLocations.value.length > 0 || validSpaces.value.length > 0
);

const resolvedSpaceLabel = computed(
  () => props.spaceMarkerLabel || t('EVENTS_REPORTS.MAP_SPACE_MARKER')
);

const pinColor = location =>
  location.color || location.stageColor || DEFAULT_PIN_COLOR;

const syncTheme = () => {
  if (typeof document === 'undefined') return;
  isDark.value =
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark');
};

const ensureMarkerCluster = async () => {
  if (clusterReady) return;
  if (typeof window !== 'undefined') {
    window.L = L;
  }
  const mod = await import('leaflet.markercluster');
  if (!L.markerClusterGroup && mod?.MarkerClusterGroup) {
    L.MarkerClusterGroup = mod.MarkerClusterGroup;
    L.markerClusterGroup = options => new mod.MarkerClusterGroup(options);
  }
  clusterReady = true;
};

const createPinIcon = color =>
  L.divIcon({
    className: 'contact-map-marker',
    html: `<div class="contact-map-marker__dot" style="background-color:${color}"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
  });

const createClusterIcon = cluster => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 'sm' : count < 50 ? 'md' : 'lg';
  return L.divIcon({
    className: 'contact-map-cluster',
    html: `<div class="contact-map-cluster__pin contact-map-cluster__pin--${size}">${count}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const locationPopupHtml = location => {
  const color = pinColor(location);
  const title = location.title || location.contact_name || '—';
  const subtitle = location.subtitle || location.company_name;
  const meta = location.meta || location.location_label;
  const body = location.body || location.event_name;
  const stage = location.stage || location.system_stage;

  const subtitleHtml = subtitle
    ? `<p class="rk-map-popup__body">${escapeHtml(subtitle)}</p>`
    : '';
  const metaHtml = meta
    ? `<p class="rk-map-popup__meta">${escapeHtml(meta)}</p>`
    : '';
  const bodyHtml = body
    ? `<p class="rk-map-popup__body">${escapeHtml(body)}</p>`
    : '';
  const stageHtml = stage
    ? `<p class="rk-map-popup__stage capitalize">
        <span class="rk-map-popup__dot" style="background-color:${color}"></span>
        ${escapeHtml(stage)}
      </p>`
    : '';

  return `<div class="rk-map-popup">
    <p class="rk-map-popup__title">${escapeHtml(title)}</p>
    ${subtitleHtml}
    ${metaHtml}
    ${bodyHtml}
    ${stageHtml}
  </div>`;
};

const createSpaceIcon = () => {
  spaceIcon.value = L.divIcon({
    className: 'space-map-marker',
    html: `<div class="space-map-marker__pin" title="space">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
        <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
        <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
        <path d="M10 6h4"/>
        <path d="M10 10h4"/>
        <path d="M10 14h4"/>
        <path d="M10 18h4"/>
      </svg>
    </div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    popupAnchor: [0, -30],
  });
};

const createLayerGroup = () => {
  if (typeof L.markerClusterGroup === 'function') {
    return L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 13,
      maxClusterRadius: 56,
      iconCreateFunction: createClusterIcon,
    });
  }
  return L.featureGroup();
};

const syncClusterMarkers = async () => {
  const map = leafletObject.value;
  if (!map) return;

  await ensureMarkerCluster();

  if (clusterGroup) {
    map.removeLayer(clusterGroup);
    clusterGroup = null;
  }

  clusterGroup = createLayerGroup();

  validLocations.value.forEach(location => {
    const marker = L.marker([location.lat, location.lng], {
      icon: createPinIcon(pinColor(location)),
    });
    marker.bindPopup(locationPopupHtml(location));
    clusterGroup.addLayer(marker);
  });

  map.addLayer(clusterGroup);
};

const onMapReady = async map => {
  leafletObject.value = map;
  createSpaceIcon();
  await syncClusterMarkers();
  fitToMarkers();
};

const fitToMarkers = async () => {
  await nextTick();
  const map = leafletObject.value;
  if (!map) return;

  const points = [
    ...validLocations.value.map(location => [location.lat, location.lng]),
    ...validSpaces.value.map(space => [space.lat, space.lng]),
  ];
  if (!points.length) return;

  const bounds = L.latLngBounds(points);
  if (bounds.isValid()) {
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 12 });
  }
};

watch(validLocations, async () => {
  await syncClusterMarkers();
  fitToMarkers();
});

watch(validSpaces, () => {
  fitToMarkers();
});

onMounted(() => {
  syncTheme();
  themeObserver = new MutationObserver(syncTheme);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onUnmounted(() => {
  themeObserver?.disconnect();
  if (clusterGroup && leafletObject.value) {
    leafletObject.value.removeLayer(clusterGroup);
  }
  clusterGroup = null;
});
</script>

<template>
  <div v-if="!hasMapContent" class="flex items-center justify-center h-full text-n-slate-11">
    —
  </div>
  <template v-else>
    <div
      class="locations-map relative w-full rounded-xl border border-n-weak overflow-hidden bg-n-slate-2"
      :class="[mapClass, { 'locations-map--dark': isDark }]"
    >
      <div
        v-if="showBasemapToggle"
        class="absolute top-3 right-3 z-[1000] flex items-center gap-0.5 p-0.5 rounded-lg bg-n-solid-2/95 border border-n-weak shadow-sm backdrop-blur-sm print:hidden"
        role="group"
        :aria-label="basemapLabel"
      >
        <button
          type="button"
          class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-150"
          :class="
            basemapStyle === 'calm'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
          "
          @click="basemapStyle = 'calm'"
        >
          {{ calmLabel }}
        </button>
        <button
          type="button"
          class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-150"
          :class="
            basemapStyle === 'standard'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
          "
          @click="basemapStyle = 'standard'"
        >
          {{ standardLabel }}
        </button>
      </div>

      <LMap
        ref="mapRef"
        class="h-full w-full"
        :zoom="zoom"
        :center="center"
        :use-global-leaflet="true"
        @ready="onMapReady"
      >
        <LTileLayer
          :key="tileUrl"
          :url="tileUrl"
          :attribution="tileAttribution"
          :max-zoom="19"
        />

        <template v-if="spaceIcon">
          <LMarker
            v-for="(space, idx) in validSpaces"
            :key="`space-${space.id || idx}`"
            :lat-lng="[space.lat, space.lng]"
            :icon="spaceIcon"
          >
            <LPopup>
              <div class="rk-map-popup">
                <p class="rk-map-popup__title">
                  {{ space.name }}
                </p>
                <p v-if="space.venue_name" class="rk-map-popup__meta">
                  {{ space.venue_name }}
                </p>
                <p class="rk-map-popup__badge">
                  {{ resolvedSpaceLabel }}
                </p>
              </div>
            </LPopup>
          </LMarker>
        </template>
      </LMap>
    </div>

    <div
      v-if="showLegend && (legendItems.length || validSpaces.length)"
      class="flex flex-wrap items-center gap-2 mt-3"
    >
      <span
        v-for="item in legendItems"
        :key="item.id"
        class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-n-slate-12 bg-n-alpha-2"
      >
        <span
          class="size-2 rounded-full flex-shrink-0"
          :style="{ backgroundColor: item.color || DEFAULT_PIN_COLOR }"
        />
        {{ item.label }}
      </span>
      <span
        v-if="validSpaces.length"
        class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-n-slate-12 bg-n-alpha-2"
      >
        <span
          class="size-2 rounded-full flex-shrink-0"
          :style="{ backgroundColor: BRAND_PURPLE }"
        />
        {{ resolvedSpaceLabel }}
      </span>
    </div>
  </template>
</template>

<style scoped>
.locations-map {
  --rk-map-brand: v-bind(BRAND_PURPLE);
  --rk-map-brand-soft: v-bind(BRAND_SOFT);
}

.locations-map :deep(.leaflet-container) {
  background: transparent;
  font-family: Inter, ui-sans-serif, system-ui, sans-serif;
  height: 100%;
  width: 100%;
}

.locations-map :deep(a) {
  padding: 0 !important;
}

.locations-map :deep(img) {
  max-width: none !important;
  max-height: none !important;
}

.locations-map :deep(.leaflet-control-zoom) {
  border: none !important;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.06), 0 4px 12px rgb(15 23 42 / 0.06) !important;
  border-radius: 0.75rem !important;
  overflow: hidden;
  margin: 12px !important;
}

.locations-map :deep(.leaflet-control-zoom a) {
  width: 32px;
  height: 32px;
  line-height: 32px;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  display: block;
  background-color: #fff !important;
  color: #334155 !important;
  border: none !important;
  text-decoration: none;
  cursor: pointer;
}

.locations-map :deep(.leaflet-control-zoom a:hover) {
  background-color: #f8fafc !important;
  color: #0f172a !important;
}

.locations-map :deep(.leaflet-control-zoom a + a) {
  border-top: 1px solid #e2e8f0 !important;
}

.locations-map--dark :deep(.leaflet-control-zoom a) {
  background-color: #1e293b !important;
  color: #e2e8f0 !important;
}

.locations-map--dark :deep(.leaflet-control-zoom a:hover) {
  background-color: #334155 !important;
  color: #f8fafc !important;
}

.locations-map--dark :deep(.leaflet-control-zoom a + a) {
  border-top-color: #334155 !important;
}

.locations-map :deep(.leaflet-control-attribution) {
  background: rgb(255 255 255 / 0.82) !important;
  color: #64748b !important;
  font-size: 10px;
  padding: 2px 8px !important;
  border-radius: 0.5rem 0 0 0;
  margin: 0 !important;
  backdrop-filter: blur(6px);
}

.locations-map :deep(.leaflet-control-attribution a) {
  color: #7045ae !important;
}

.locations-map--dark :deep(.leaflet-control-attribution) {
  background: rgb(15 23 42 / 0.78) !important;
  color: #94a3b8 !important;
}

.locations-map :deep(.leaflet-popup-content-wrapper) {
  background: #fff;
  color: #0f172a;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.04), 0 8px 24px rgb(15 23 42 / 0.08);
  padding: 0;
}

.locations-map :deep(.leaflet-popup-content) {
  margin: 12px 14px;
  line-height: 1.4;
  min-width: 140px;
}

.locations-map :deep(.leaflet-popup-tip) {
  background: #fff;
  border: none;
  box-shadow: none;
}

.locations-map :deep(.leaflet-popup-close-button) {
  color: #94a3b8 !important;
  top: 6px !important;
  right: 6px !important;
  width: 22px !important;
  height: 22px !important;
  font-size: 16px !important;
  font-weight: 400 !important;
  border-radius: 9999px;
}

.locations-map :deep(.leaflet-popup-close-button:hover) {
  color: #0f172a !important;
  background: #f1f5f9;
}

.locations-map--dark :deep(.leaflet-popup-content-wrapper),
.locations-map--dark :deep(.leaflet-popup-tip) {
  background: #1e293b;
  color: #f8fafc;
  border-color: #334155;
}

.locations-map--dark :deep(.leaflet-popup-close-button:hover) {
  color: #f8fafc !important;
  background: #334155;
}

.locations-map :deep(.rk-map-popup__title) {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.locations-map :deep(.rk-map-popup__body) {
  margin: 0 0 2px;
  font-size: 12px;
  color: #334155;
}

.locations-map :deep(.rk-map-popup__meta) {
  margin: 0 0 2px;
  font-size: 12px;
  color: #64748b;
}

.locations-map :deep(.rk-map-popup__badge) {
  margin: 8px 0 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #7045ae;
  background: #efedf8;
}

.locations-map :deep(.rk-map-popup__stage) {
  margin: 8px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.locations-map :deep(.rk-map-popup__dot) {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  flex-shrink: 0;
}

.locations-map--dark :deep(.rk-map-popup__title) {
  color: #f8fafc;
}

.locations-map--dark :deep(.rk-map-popup__body) {
  color: #cbd5e1;
}

.locations-map--dark :deep(.rk-map-popup__meta),
.locations-map--dark :deep(.rk-map-popup__stage) {
  color: #94a3b8;
}

.locations-map--dark :deep(.rk-map-popup__badge) {
  color: #c8c0dd;
  background: rgb(112 69 174 / 0.22);
}

.locations-map :deep(.space-map-marker),
.locations-map :deep(.contact-map-marker),
.locations-map :deep(.contact-map-cluster) {
  background: transparent;
  border: none;
}

.locations-map :deep(.contact-map-marker__dot) {
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgb(15 23 42 / 0.25);
}

.locations-map--dark :deep(.contact-map-marker__dot) {
  border-color: #1e293b;
}

.locations-map :deep(.contact-map-cluster__pin) {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #7045ae;
  color: #fff;
  font-family: Inter, ui-sans-serif, system-ui, sans-serif;
  font-weight: 600;
  border: 2px solid #fff;
  box-shadow: 0 1px 2px rgb(112 69 174 / 0.25), 0 6px 16px rgb(112 69 174 / 0.28);
}

.locations-map :deep(.contact-map-cluster__pin--sm) {
  width: 34px;
  height: 34px;
  font-size: 12px;
}

.locations-map :deep(.contact-map-cluster__pin--md) {
  width: 40px;
  height: 40px;
  font-size: 13px;
}

.locations-map :deep(.contact-map-cluster__pin--lg) {
  width: 46px;
  height: 46px;
  font-size: 14px;
}

.locations-map--dark :deep(.contact-map-cluster__pin) {
  border-color: #1e293b;
}

.locations-map :deep(.space-map-marker__pin) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  background: #7045ae;
  color: #fff;
  box-shadow: 0 1px 2px rgb(112 69 174 / 0.25), 0 6px 16px rgb(112 69 174 / 0.28);
  border: 2px solid #fff;
}

.locations-map--dark :deep(.space-map-marker__pin) {
  border-color: #1e293b;
}
</style>
